import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';

import AppButton from '../../../../components/designe/app-button/AppButton';
import AppAutocomplete from '../../../../components/designe/app-autocomplete/AppAutocomplete';
import AppFileContainer from '../../../../components/designe/app-file-container/AppFileContainer';
import AppSwitchWithLabel from '../../../../components/designe/app-switch-with-label/AppSwitchWithLabel';
import { useStyles } from '../Sync.styles';
import theme from '../../../../styles/theme';
import type {
  ExerciseSyncItemDto,
  SyncExerciseRequestDto,
} from '../../../../services/redux/api/syncApi';
import AppFormTextField from '../../../../components/designe/app-form-text-field';
import AppFormSelectWrapper from '../../../../components/designe/app-form-select-wrapper';
import { useSyncExerciseForm } from './useSyncExerciseForm';
import { IExerciseProperty } from '../../../../types/interfaces/exercise-property';
import { IExerciseLoadFormula } from '../../../../types/interfaces/exercise-load-formula';
import { INewFile } from '../../../../types/interfaces/new-file';
import { getExerciseOptionUnitBracketSuffix } from '../../../../utils/exercise-option-display';

interface SyncExerciseFormProps {
  item: ExerciseSyncItemDto | null;
  provider: string;
  onSave: (
    body: SyncExerciseRequestDto,
    extra?: { filesToUpload: INewFile[]; avatarFileId: string | number | null },
  ) => void;
  onDirtyChange: (dirty: boolean) => void;
  isSaving: boolean;
}

export function SyncExerciseForm({
  item,
  provider,
  onSave,
  onDirtyChange,
  isSaving,
}: SyncExerciseFormProps) {
  const { t } = useTranslation();
  const { classes } = useStyles(theme);

  const {
    formik,
    mode,
    setMode,
    canSave,
    muscles,
    equipments,
    developedSkills,
    categoryOptions,
    muscleOptions,
    equipmentOptions,
    linkOptions,
    languageOptions,
    selectedAdditionalMuscles,
    selectedAdditionalEquipments,
    selectedDevelopedSkills,
    exerciseOptions,
    onFilesToUploadChanged,
    onMainFileChanged,
    onChangeFormikOption,
    availableFormulas,
  } = useSyncExerciseForm({ item, provider, onSave, onDirtyChange });

  if (!item) {
    return (
      <div className={classes.formColumn}>
        <div className={classes.panelTitle}>
          {t('adminDashboard.sync.formTitle')}
        </div>
        <div className={classes.formContent}>
          {t('adminDashboard.sync.selectItem')}
        </div>
      </div>
    );
  }

  return (
    <div className={classes.formColumn}>
      <div className={classes.panelTitle}>
        {t('adminDashboard.sync.formTitle')}
      </div>
      <div className={classes.formContent}>
        <form
          onSubmit={(e) => {
            if (isSaving) {
              e.preventDefault();
              return;
            }
            formik.handleSubmit(e);
          }}>
          <RadioGroup
            row
            value={mode}
            onChange={(_, v) => setMode(v as 'link' | 'create')}>
            <FormControlLabel
              value="link"
              control={<Radio size="small" />}
              label={t('adminDashboard.sync.linkExisting')}
            />
            <FormControlLabel
              value="create"
              control={<Radio size="small" />}
              label={t('adminDashboard.sync.createNew')}
            />
          </RadioGroup>

          {mode === 'link' && (
            <AppFormSelectWrapper
              label={t('adminDashboard.sync.selectExercise')}
              name="localResourceId"
              options={linkOptions}
              fieldName="label"
              disableClear
              formik={formik}
              onChange={(e) => {
                const val = e?.target?.value;
                formik.setFieldValue('localResourceId', val?.id ?? '');
              }}
            />
          )}

          {mode === 'create' && (
            <>
              <AppFormTextField
                label={t('common.title')}
                fullWidth
                name="createTitle"
                formik={formik}
              />
              <Box sx={{ minHeight: 120 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t('exercises.photos')}
                </Typography>
                <AppFileContainer
                  key={item.providerResourceId}
                  files={[]}
                  editMode
                  onFilesToUploadChanged={onFilesToUploadChanged}
                  onMainFileChanged={onMainFileChanged}
                  canSetAsMainFile={(file) =>
                    String(file.mimeType || '').toLowerCase() !== 'image/gif'
                  }
                  mainFileId={
                    formik.values.createAvatarFileId != null
                      ? String(formik.values.createAvatarFileId)
                      : undefined
                  }
                />
                {formik.errors?.createAvatarFileId && (
                  <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                    {formik.errors.createAvatarFileId}
                  </Typography>
                )}
              </Box>
              <AppFormTextField
                label={t('common.description')}
                fullWidth
                name="createDescription"
                formik={formik}
                multiline
              />
              <AppFormSelectWrapper
                label={t('exercises.language')}
                name="createLanguageId"
                options={languageOptions}
                fieldName="name"
                disableClear
                formik={formik}
                onChange={(e) => {
                  const val = e?.target?.value;
                  formik.setFieldValue('createLanguageId', val?.id ?? '');
                }}
              />
              <AppFormSelectWrapper
                label={t('exercises.category')}
                name="createCategoryId"
                options={categoryOptions}
                fieldName="name"
                disableClear
                formik={formik}
                onChange={(e) => {
                  const val = e?.target?.value;
                  formik.setFieldValue('createCategoryId', val?.id ?? '');
                }}
              />
              <AppFormSelectWrapper
                label={t('exercises.mainMuscleGroup')}
                name="createMainMuscleGroupId"
                options={muscleOptions}
                fieldName="name"
                disableClear
                formik={formik}
                onChange={(e) => {
                  const val = e?.target?.value;
                  formik.setFieldValue(
                    'createMainMuscleGroupId',
                    val?.id ?? '',
                  );
                }}
              />
              <AppAutocomplete
                multiple
                disableCloseOnSelect
                options={muscles.filter(
                  (m) => m.id !== formik.values.createMainMuscleGroupId,
                )}
                value={selectedAdditionalMuscles}
                onChange={(_, value) => {
                  formik.setFieldValue(
                    'createAdditionalMuscleIds',
                    (value as IExerciseProperty[]).map((v) => v.id),
                  );
                }}
                getOptionLabel={(option: IExerciseProperty) =>
                  option.name || option.nameKey || ''
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('exercises.additionalMuscles')}
                    margin="dense"
                    variant="outlined"
                  />
                )}
              />
              <AppFormSelectWrapper
                label={t('exercises.equipment')}
                name="createEquipmentId"
                options={equipmentOptions}
                fieldName="name"
                disableClear
                formik={formik}
                onChange={(e) => {
                  const val = e?.target?.value;
                  formik.setFieldValue('createEquipmentId', val?.id ?? '');
                }}
              />
              <AppAutocomplete
                multiple
                disableCloseOnSelect
                options={equipments.filter(
                  (e) => e.id !== formik.values.createEquipmentId,
                )}
                value={selectedAdditionalEquipments}
                onChange={(_, value) => {
                  formik.setFieldValue(
                    'createAdditionalEquipmentIds',
                    (value as IExerciseProperty[]).map((v) => v.id),
                  );
                }}
                getOptionLabel={(option: IExerciseProperty) =>
                  option.name || option.nameKey || ''
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('exercises.additionalEquipments')}
                    margin="dense"
                    variant="outlined"
                  />
                )}
              />
              <AppAutocomplete
                multiple
                disableCloseOnSelect
                options={developedSkills}
                value={selectedDevelopedSkills}
                onChange={(_, value) => {
                  formik.setFieldValue(
                    'createDevelopedSkillIds',
                    (value as IExerciseProperty[]).map((v) => v.id),
                  );
                }}
                getOptionLabel={(option: IExerciseProperty) =>
                  option.name || option.nameKey || ''
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('exercises.developedSkills')}
                    margin="dense"
                    variant="outlined"
                  />
                )}
              />
              <AppFormSelectWrapper
                label={t('exercises.formulaForCalculationLoad')}
                name="createExerciseLoadFormulaId"
                options={availableFormulas}
                fieldName="name"
                disableSearch
                formik={formik}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const val = e?.target
                    ?.value as unknown as IExerciseLoadFormula;
                  formik.setFieldValue(
                    'createExerciseLoadFormulaId',
                    val?.id ?? '',
                  );
                }}
              />
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t('exercises.options')}
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <AppSwitchWithLabel
                      labelPlacement="end"
                      name="createSplitBySide"
                      value={formik.values.createSplitBySide}
                      label={t('exercises.splitBySide')}
                      checked={formik.values.createSplitBySide}
                      onChange={(e) =>
                        formik.setFieldValue(
                          'createSplitBySide',
                          e.target.checked,
                        )
                      }
                    />
                  </Grid>
                  {exerciseOptions?.map((option) =>
                    option.id ? (
                      <Grid item xs={6} key={option.id}>
                        <AppSwitchWithLabel
                          labelPlacement="end"
                          name="createOptionIds"
                          value={formik.values.createOptionIds.includes(
                            option.id,
                          )}
                          label={`${option.name || option.nameKey || ''}${getExerciseOptionUnitBracketSuffix(option)}`}
                          checked={formik.values.createOptionIds.includes(
                            option.id,
                          )}
                          onChange={(e) =>
                            onChangeFormikOption(e, { id: option.id })
                          }
                        />
                      </Grid>
                    ) : null,
                  )}
                </Grid>
              </Box>
            </>
          )}

          <AppButton
            type="submit"
            variant="contained"
            disabled={!canSave || isSaving}
            loader={isSaving}>
            {t('common.save')}
          </AppButton>
        </form>
      </div>
    </div>
  );
}
